import { useState, useEffect, useContext } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { MapPin, Building2, TrendingUp, FileText, ArrowLeft, Mail, Handshake } from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import { VerifiedBadge, VerifiedBadgeFull } from "@/components/VerifiedBadge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dealDialog, setDealDialog] = useState(false);
  const [dealMessage, setDealMessage] = useState("");
  const [creatingDeal, setCreatingDeal] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
    } catch (error) {
      toast.error("Failed to fetch listing");
    } finally {
      setLoading(false);
    }
  };

  const handleContactSeller = () => {
    if (!user) {
      toast.error("Please login to contact seller");
      navigate("/auth");
      return;
    }
    navigate("/messages", { state: { receiverId: listing.seller_id, receiverName: listing.seller_name } });
  };

  const handleStartDeal = () => {
    if (!user) {
      toast.error("Please login to start a deal");
      navigate("/auth");
      return;
    }
    if (listing.seller_id === user.id) {
      toast.error("You cannot start a deal on your own listing");
      return;
    }
    setDealMessage(`Hi, I'm interested in your business "${listing.name}". I'd like to learn more about this opportunity and potentially discuss terms.`);
    setDealDialog(true);
  };

  const handleCreateDeal = async () => {
    setCreatingDeal(true);
    try {
      const { data } = await api.post("/deals", {
        listing_id: listing.id,
        message: dealMessage
      });
      toast.success("Deal started successfully! You can now track it in My Deals.");
      setDealDialog(false);
      navigate(`/deals/${data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start deal");
    } finally {
      setCreatingDeal(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-12">Loading...</div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p>Listing not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/marketplace" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Marketplace
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <div data-testid="listing-detail-header">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-4xl font-bold text-slate-900 mb-2">{listing.name}</h1>
                  <div className="flex items-center gap-2 text-slate-600">
                    <MapPin className="w-4 h-4" />
                    {listing.location}
                  </div>
                </div>
                {listing.verified && (
                  <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Verified</Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Badge variant="outline">{listing.industry}</Badge>
                <Badge variant="outline">{listing.views} views</Badge>
              </div>
            </div>

            {/* Images */}
            {listing.images && listing.images.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {listing.images.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`${listing.name} ${idx + 1}`}
                    className="rounded-lg w-full aspect-video object-cover"
                  />
                ))}
              </div>
            )}

            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Description
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed">{listing.description}</p>
              </CardContent>
            </Card>

            {/* Financial Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Financial Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Annual Revenue</p>
                    <p className="text-2xl font-bold text-slate-900">{formatCurrency(listing.revenue)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Annual Profit</p>
                    <p className="text-2xl font-bold text-emerald-600">{formatCurrency(listing.profit)}</p>
                  </div>
                  {listing.assets && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Assets</p>
                      <p className="text-slate-700">{listing.assets}</p>
                    </div>
                  )}
                  {listing.liabilities && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Liabilities</p>
                      <p className="text-slate-700">{listing.liabilities}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Business Documents */}
            {listing.documents && listing.documents.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Business Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {listing.documents.map((doc, index) => (
                      <a
                        key={index}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                        data-testid={`view-document-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded">
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                            <p className="text-xs text-slate-500">{doc.type}</p>
                          </div>
                        </div>
                        <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="sticky top-6">
              <CardHeader>
                <CardTitle className="text-3xl font-bold text-slate-900">{formatCurrency(listing.asking_price)}</CardTitle>
                <p className="text-slate-600">Asking Price</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Seller</p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900">{listing.seller_name}</p>
                    {listing.seller_verified && <VerifiedBadgeFull />}
                  </div>
                </div>
                
                {/* Show "I'm Interested" button only if not the seller */}
                {user?.id !== listing.seller_id && (
                  <Button
                    onClick={handleStartDeal}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="start-deal-button"
                  >
                    <Handshake className="w-4 h-4 mr-2" />
                    I'm Interested - Start Deal
                  </Button>
                )}
                
                <Button
                  onClick={handleContactSeller}
                  className="w-full"
                  variant={user?.id !== listing.seller_id ? "outline" : "default"}
                  data-testid="contact-seller-button"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Seller
                </Button>
                
                {user?.id === listing.seller_id && (
                  <p className="text-sm text-slate-500 text-center">This is your listing</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Start Deal Dialog */}
      <Dialog open={dealDialog} onOpenChange={setDealDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="w-5 h-5 text-emerald-600" />
              Start a Deal
            </DialogTitle>
            <DialogDescription>
              Express your interest in "{listing?.name}". This will create a deal that both parties can track through the acquisition process.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-500 mb-1">Asking Price</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(listing?.asking_price)}</p>
            </div>
            <div>
              <Label htmlFor="deal-message">Message to Seller</Label>
              <Textarea
                id="deal-message"
                placeholder="Introduce yourself and express your interest..."
                value={dealMessage}
                onChange={(e) => setDealMessage(e.target.value)}
                rows={4}
                className="mt-2"
              />
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>What happens next?</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• The seller will be notified of your interest</li>
                <li>• You can track the deal progress in "My Deals"</li>
                <li>• Both parties can advance through stages: NDA → Due Diligence → LOI → Closing</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDealDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateDeal} 
              disabled={creatingDeal}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {creatingDeal ? "Creating..." : "Start Deal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}